import tokens from '@verifime/design-tokens';
import React, { ReactNode } from 'react';
import { Control, FieldErrorsImpl, FieldValues, UseFormSetError } from 'react-hook-form';
import FlexBox from '../../../FlexBox';
import ResponsiveBox from '../../../ResponsiveBox';
import { TFieldsOperations, TFromFieldInfo } from '../../FormGenerator/utils';
import { fieldComponentMapping } from '../common/mapping';
import { TFormFieldsAndRules } from '../common/utils';

/**
 *
 * To make this change affections as less as possible,
 * do not change existing GenerateFormFields,
 * just reorganise form fields and rules in this component
 *
 * TODO:
 *  1. delete the old component `GenerateFormFields`
 *  2. rename this component to `GenerateFormFields`
 *  3. change individual & organisation form fields and rules files to match this new component
 */
export default function GenerateFormFields<TFieldValues extends FieldValues = FieldValues>({
  formFieldsAndRules,
  control,
  errors,
  setError,
  formFieldsWithDefaultValues,
  fieldsOperations,
  defaultMargin = 'normal',
  defaultSize = 'medium',
  ContainerComponent: ContainerComponentProp,
  OuterContainerComponent: OuterContainerComponentProp,
}: Readonly<{
  formFieldsAndRules: TFormFieldsAndRules<keyof TFieldValues>;
  control: Control<TFieldValues, any>;
  setError?: UseFormSetError<TFieldValues>;
  errors: Partial<FieldErrorsImpl<Record<keyof TFieldValues, any>>>;
  formFieldsWithDefaultValues?: Record<string, any>;
  /**
   *
   * TODO: remove this the functionality has covered by fieldInfo.onValueChange
   *
   * There are maybe several Select to be rendered in the `formFieldsAndRules`,
   * each of them may have different `initial value`, `onOptionChange` operations,
   * and those `onOptionChange` even `initial value` cannot be predefined in the `formFieldsAndRules`,
   * thus expose this to accept different operations of fields
   */
  fieldsOperations?: TFieldsOperations<TFieldValues>;
  ContainerComponent?: React.FunctionComponent<any>;
  OuterContainerComponent?: React.FunctionComponent<any>;
  defaultSize?: TFromFieldInfo['size'];
  defaultMargin?: TFromFieldInfo['margin'];
}>) {
  // Both must run on every render regardless of whether the caller supplies them, so they are
  // hooks called unconditionally in the body rather than default parameter values (a default
  // expression only evaluates when its prop is omitted, which made each hook's call count depend
  // on the caller, not just on this component's own render). Each default is applied here at the
  // point of use instead.
  const defaultContainerComponent = React.useCallback(
    ({ children }: { children: ReactNode }) => (
      <ResponsiveBox verticalAlign="middle">{children}</ResponsiveBox>
    ),
    [ResponsiveBox],
  );
  const ContainerComponent = ContainerComponentProp ?? defaultContainerComponent;

  const defaultOuterContainerComponent = React.useCallback(
    ({ children }: { children: ReactNode }) => (
      <FlexBox direction="column" sx={{ gap: tokens.spacingXs }}>
        {children}
      </FlexBox>
    ),
    [FlexBox, tokens.spacingXs],
  );
  const OuterContainerComponent = OuterContainerComponentProp ?? defaultOuterContainerComponent;

  if (Object.keys(formFieldsAndRules || {}).length < 1) {
    return null;
  }

  return (
    <OuterContainerComponent>
      {Object.entries(formFieldsAndRules).map(([rowNumber, fieldsInfo]) => {
        const fieldsInfoWithDefaults = fieldsInfo.map((fieldInfo) => ({
          ...fieldInfo,
          size: fieldInfo.size ?? defaultSize,
          margin: fieldInfo.margin ?? defaultMargin,
        }));
        return (
          <ContainerComponent key={rowNumber}>
            {fieldsInfoWithDefaults.map((fieldInfo) => {
              const {
                initValue,
                onOptionChange,
                onFileChange,
                disabled,
                onTextChange,
                onDateChange,
                onDateRangeChange,
                onCheckBoxChange,
                onRadioChange,
                onNumberChange,
              } = fieldsOperations?.[fieldInfo.fieldName] || {};
              return (
                <React.Fragment key={String(fieldInfo.fieldName)}>
                  {fieldInfo.renderComponent
                    ? fieldInfo.renderComponent()
                    : fieldComponentMapping[fieldInfo.renderType]?.({
                        control,
                        setError,
                        errors,
                        fieldInfo,
                        formFieldsWithDefaultValues,
                        onOptionChange,
                        initValue,
                        onFileChange,
                        disabled,
                        onTextChange,
                        onDateChange,
                        onDateRangeChange,
                        onCheckBoxChange,
                        onRadioChange,
                        onNumberChange,
                      })}
                </React.Fragment>
              );
            })}
          </ContainerComponent>
        );
      })}
    </OuterContainerComponent>
  );
}
